import type React from "react";
import ReactMarkdown from "react-markdown";
import classnames from "classnames";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import { createUseStyles } from "@/theme";

// NB! Do not use this component as standalone, it's meant to be used in generic
export type TextStyleProps = {
  bold?: boolean;
  thin?: boolean;
  error?: boolean;
  brandColor?: boolean;
  black?: boolean;
  white?: boolean;
  gray1?: boolean;
  gray2?: boolean;
  gray3?: boolean;
  gray4?: boolean;
  gray5?: boolean;
  gray6?: boolean;
  isButton?: boolean;
};

const useStyles = createUseStyles<TextStyleProps>(
  ({ color, font, spacing }) => {
    const getColor = ({
      black,
      error,
      white,
      gray1,
      gray2,
      gray3,
      gray4,
      gray5,
      gray6,
      brandColor,
    }: TextStyleProps) => {
      if (error) return color.error;
      if (brandColor) return color.primaryBrand;
      if (black) return color.black;
      if (white) return color.white;
      if (gray1) return color.gray1;
      if (gray2) return color.gray2;
      if (gray3) return color.gray3;
      if (gray4) return color.gray4;
      if (gray5) return color.gray5;
      if (gray6) return color.gray6;
      return "inherit";
    };

    return {
      textStyles: {
        display: ({ isButton }) => {
          if (isButton) return "inherit !important";
          return "contents";
        },
        "& ::marker": {
          fontSize: "16px",
        },
        "& li": {
          whiteSpace: "normal",
          // Loose markdown lists wrap each item's text in a <p>, which the
          // parent P component renders as display: inline-block. An
          // inline-block's baseline is its LAST line box, so the outside list
          // marker aligns to the bottom line of a wrapped item. Force block so
          // the marker tracks the first line.
          "& p": {
            display: "block",
          },
        },
        "& ul": {
          display: "flex",
          flexDirection: "column",
          paddingLeft: spacing.l,
          color: (props) => getColor(props),
          "& li": {
            fontSize: "inherit",
            lineHeight: "inherit",
          },
        },
        "&, & p": {
          fontWeight: ({ bold, thin }) => {
            if (bold) return font.weight.m;
            if (thin) return font.weight.s;
            return;
          },
          color: (props) => getColor(props),
        },
      },
      // Preserves literal "\n" line breaks in plain-text children (e.g.
      // GiftSlogan, multi-line translation strings). NOT applied to the
      // markdown branch, where react-markdown emits inter-block newline text
      // nodes that pre-wrap would otherwise render as spurious blank lines.
      plainText: {
        whiteSpace: "pre-wrap",
      },
      markdownStyles: {
        "& table": {
          borderCollapse: "collapse",
          width: "100%",
          border: `1px solid ${color.gray5}`,
        },
        "& th, & td": {
          border: `1px solid ${color.gray5}`,
          padding: spacing.m,
          textAlign: "left",
          verticalAlign: "top",
          // The app sets a global `word-break: break-word`, which makes the
          // auto table layout collapse columns to a single character. Reset to
          // size columns by whole words; keep `overflow-wrap` so long tokens
          // (e.g. URLs) still wrap instead of overflowing the cell.
          wordBreak: "normal",
          overflowWrap: "break-word",
        },
        "& th": {
          fontWeight: font.weight.m,
          backgroundColor: color.gray6,
        },
      },
    };
  },
);

type TextComponentProps = TextStyleProps & {
  children?: React.ReactNode;
  cmsContent?: string;
  classNames?: string;
};

const TextComponent: React.FC<TextComponentProps> = ({
  children,
  cmsContent,
  classNames,
  ...props
}) => {
  const classes = useStyles(props);

  if (cmsContent) {
    return (
      <span
        className={classnames([
          classes.textStyles,
          classes.markdownStyles,
          classNames,
        ])}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {cmsContent}
        </ReactMarkdown>
      </span>
    );
  }

  return (
    <span
      className={classnames([
        classes.textStyles,
        classes.plainText,
        classNames,
      ])}
    >
      {children}
    </span>
  );
};

export default TextComponent;
